import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { apiGet } from '../lib/api';
import { formatNTD, formatDate } from '@yiweiwu/shared';
import type { MemberSummary } from '../lib/types';
import { FilterBar } from '../components/FilterBar';
import { DataTable, type Column } from '../components/DataTable';

interface PaginatedMembers {
  items: MemberSummary[];
  total: number;
  page: number;
  pageSize: number;
}

export default function Members() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<PaginatedMembers | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const search = searchParams.get('search') ?? '';
  const page = parseInt(searchParams.get('page') ?? '1', 10);

  const fetchMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: Record<string, string> = {
        page: String(page),
        pageSize: '20',
      };
      if (search) params.search = search;

      const result = await apiGet<PaginatedMembers>('/members', params);
      setData(result);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : '載入會員失敗');
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const handleClear = () => setSearchParams({});

  const columns: Column<MemberSummary>[] = [
    {
      key: 'name',
      header: '會員',
      render: (row) => (
        <Link
          to={`/admin/members/${row.id}`}
          className="font-medium text-brand-gold hover:underline"
        >
          {row.name}
        </Link>
      ),
    },
    {
      key: 'phone',
      header: '電話',
      render: (row) => (
        <span className="text-gray-600">{row.phone ?? '-'}</span>
      ),
    },
    {
      key: 'email',
      header: '電子郵件',
      render: (row) => (
        <span className="text-gray-600">{row.email ?? '-'}</span>
      ),
    },
    {
      key: 'orderCount',
      header: '訂單數',
      render: (row) => (
        <span className="font-medium text-gray-900">{row.orderCount}</span>
      ),
    },
    {
      key: 'totalSpent',
      header: '總消費',
      render: (row) => (
        <span className="font-medium text-gray-900">
          {formatNTD(row.totalSpent)}
        </span>
      ),
    },
    {
      key: 'points',
      header: '點數',
      render: (row) => (
        <span className="font-medium text-brand-gold">{row.points} 點</span>
      ),
    },
    {
      key: 'createdAt',
      header: '加入日期',
      render: (row) => formatDate(row.createdAt),
    },
    {
      key: 'actions',
      header: '操作',
      render: (row) => (
        <Link
          to={`/admin/members/${row.id}`}
          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-brand-gold hover:bg-brand-gold/10 transition-colors"
        >
          <Eye className="h-3.5 w-3.5" />
          查看
        </Link>
      ),
    },
  ];

  return (
    <div>
      {/* 頁首 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-brand-cocoa font-heading">
          會員管理
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {data ? `共 ${data.total} 位會員` : '載入中...'}
        </p>
      </div>

      {/* 搜尋列 */}
      <FilterBar
        searchPlaceholder="搜尋會員名稱、電話、Email..."
        searchValue={search}
        onSearchChange={(v) => updateParam('search', v)}
        onClear={handleClear}
      />

      {/* 錯誤訊息 */}
      {error && (
        <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          {error}
          <button
            onClick={fetchMembers}
            className="ml-3 underline hover:text-red-800"
          >
            重試
          </button>
        </div>
      )}

      {/* 資料表 */}
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        keyExtractor={(r) => r.id}
        loading={loading}
        emptyMessage="尚無會員資料"
      />

      {/* 分頁 */}
      {data && data.total > data.pageSize && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            第 {data.page} / {Math.ceil(data.total / data.pageSize)} 頁
          </span>
          <div className="flex gap-2">
            <button
              disabled={data.page <= 1}
              onClick={() => updateParam('page', String(data.page - 1))}
              className="px-3 py-1.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              上一頁
            </button>
            <button
              disabled={data.page >= Math.ceil(data.total / data.pageSize)}
              onClick={() => updateParam('page', String(data.page + 1))}
              className="px-3 py-1.5 rounded-lg border border-brand-bake bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              下一頁
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
